import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Payment Gateway and UPI payment notices (owner 2026-10-24, radaccess v1.156).
 *
 *   GET|POST /payment/gatewayapi/{ns}/accounts               radpayment List/CreateGatewayAccount
 *   GET|PUT  /payment/gatewayapi/{ns}/accounts/{id}          radpayment Get/UpdateGatewayAccount
 *   POST     /payment/gatewayapi/{ns}/accounts/{id}/verify   radpayment VerifyGatewayAccount
 *   POST     /payment/gatewayapi/{ns}/accounts/{id}/receipt-email/code    radaccess: code to the receipt email
 *   POST     /payment/gatewayapi/{ns}/accounts/{id}/receipt-email/verify  radaccess: check it, radpayment records it
 *   GET|PUT  /payment/gatewayapi/{ns}/assignments            radpayment List/SetGatewayAssignment
 *   GET      /payment/noticeapi/{ns}?status=                 radpayment ListPaymentNotices
 *   POST     /payment/noticeapi/{ns}/notice/{id}/reject      radpayment RejectPaymentNotice
 *   POST     /payment/noticeapi/{ns}/notice/{id}/apply       radaccess ApplyUPINotice
 *
 * Gateway routes: radaccess paymentgateway -- Operator, Admin (manager),
 * superadmin. Notices: every staff role reads; Operator, Admin and billing
 * reject and apply.
 *
 * 🔑 A secret goes in and never comes out: radpayment answers the public view
 * (key id, mode, status). The console never shows or keeps a secret after
 * the request that sent it.
 */

export type GatewayKind = "razorpay" | "upi_qr" | "cashfree";

/** radpayment model.GatewayAccount (public view). */
export interface GatewayAccount {
  id: string;
  namespace: string;
  connectorname: GatewayKind;
  label: string;
  mode: "test" | "live";
  /** Razorpay key id, or the UPI ID for a UPI QR account. */
  keyid: string;
  haswebhooksecret: boolean;
  status: string;
  verifiedat?: string;
  lasterror?: string;
  enabled: boolean;
  createdat: string;
  updatedat: string;
  /** The operator's receipt email (radpayment v0.1.43); "" or absent = not set yet. */
  receipt_email?: string;
  /** The receipt email was proved with a code (radpayment v0.1.45); checkout prefills it only then. */
  receipt_email_verified?: boolean;
}

/** radpayment model.GatewayAssignment. subscriberaccountid "" = the namespace default. */
export interface GatewayAssignment {
  namespace: string;
  subscriberaccountid: string;
  gatewayaccountid: string;
  updatedat: string;
  updatedby?: string;
}

export interface GatewayAssignments {
  assignments: GatewayAssignment[];
  /** What a payment here uses now, inherited from a parent too; null = none. */
  effective: { account: GatewayAccount; assignedat: string; forsubscriber: boolean } | null;
  effectiveerror?: string;
}

export interface NewGatewayAccount {
  kind: GatewayKind;
  label: string;
  /** Razorpay key id (rzp_test_… / rzp_live_…), Cashfree App ID, or the UPI ID. */
  keyId: string;
  /** Razorpay and Cashfree. */
  keySecret?: string;
  webhookSecret?: string;
  /**
   * Cashfree only: its App ID does not say test or live, so the operator
   * states it and radpayment refuses a Cashfree account without one. Never
   * sent for Razorpay (the key says it; a stated mismatch is refused).
   */
  mode?: "test" | "live";
  /** Razorpay and Cashfree: required (owner 2026-10-29). */
  receiptEmail?: string;
}

const enc = encodeURIComponent;
const gw = (ns: string) => `/payment/gatewayapi/${enc(ns)}`;

export async function listGatewayAccounts(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<GatewayAccount[]> {
  const r = await callGateway<GatewayAccount[] | null>(identity, config().gatewayBase, `${gw(ns)}/accounts`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function createGatewayAccount(identity: CallerIdentity, ns: string, input: NewGatewayAccount): Promise<GatewayAccount> {
  const body: Record<string, string> = { connector_name: input.kind, label: input.label.trim(), key_id: input.keyId.trim() };
  if (input.kind !== "upi_qr") {
    body.key_secret = (input.keySecret ?? "").trim();
    if (input.webhookSecret?.trim()) body.webhook_secret = input.webhookSecret.trim();
    body.receipt_email = (input.receiptEmail ?? "").trim();
  }
  if (input.kind === "cashfree" && input.mode) body.mode = input.mode;
  const r = await callGateway<GatewayAccount | null>(identity, config().gatewayBase, `${gw(ns)}/accounts`, { method: "POST", body });
  if (!r.data) throw new Error("The gateway account was saved but not returned; reload the page.");
  return r.data;
}

export interface GatewayAccountChange {
  label?: string;
  enabled?: boolean;
  // A new key id goes with its key secret in the same change (owner 2026-10-28).
  keyId?: string;
  // Cashfree's new key's mode (Razorpay's key says it); only with keyId.
  mode?: "test" | "live";
  keySecret?: string;
  webhookSecret?: string;
  receiptEmail?: string;
}

export async function updateGatewayAccount(identity: CallerIdentity, ns: string, id: string, change: GatewayAccountChange): Promise<GatewayAccount> {
  const body: Record<string, unknown> = {};
  if (change.label !== undefined) body.label = change.label.trim();
  if (change.enabled !== undefined) body.enabled = change.enabled;
  if (change.keyId !== undefined) body.key_id = change.keyId.trim();
  if (change.mode !== undefined) body.mode = change.mode;
  if (change.keySecret !== undefined) body.key_secret = change.keySecret.trim();
  if (change.webhookSecret !== undefined) body.webhook_secret = change.webhookSecret.trim();
  if (change.receiptEmail !== undefined) body.receipt_email = change.receiptEmail.trim();
  const r = await callGateway<GatewayAccount | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}`, { method: "PUT", body });
  if (!r.data) throw new Error("The change was saved but not returned; reload the page.");
  return r.data;
}

/** A code was emailed to the account's receipt email (owner 2026-10-29: verify it). */
export interface ReceiptEmailCode {
  status?: "already_verified";
  requestId?: string;
  expiresIn?: number;
  resendIn?: number;
  /** Masked, e.g. "o***@example.com". */
  email?: string;
}

export async function sendReceiptEmailCode(identity: CallerIdentity, ns: string, id: string): Promise<ReceiptEmailCode> {
  const r = await callGateway<ReceiptEmailCode | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}/receipt-email/code`, { method: "POST", body: {} });
  return r.data ?? {};
}

/** accessToken is requestId + "." + the code from the mail. */
export async function verifyReceiptEmail(identity: CallerIdentity, ns: string, id: string, accessToken: string): Promise<GatewayAccount> {
  const r = await callGateway<GatewayAccount | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}/receipt-email/verify`, { method: "POST", body: { accessToken } });
  if (!r.data) throw new Error("The code was checked but nothing came back; reload the page.");
  return r.data;
}

export async function verifyGatewayAccount(identity: CallerIdentity, ns: string, id: string): Promise<GatewayAccount> {
  const r = await callGateway<GatewayAccount | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}/verify`, { method: "POST", body: {} });
  if (!r.data) throw new Error("The check ran but returned nothing; reload the page.");
  return r.data;
}

/** A Rs 1 test order at the gateway (owner 2026-10-26): nothing is recorded, no receipt. */
export interface TestPayment {
  gatewayaccountid: string;
  connectorname: GatewayKind;
  mode: "test" | "live";
  keyid: string;
  connectorreference: string;
  checkoutsession?: string;
  amount: number;
  currency: string;
}

/** Start a Rs 1 test payment through one TEST-mode account (test networks only). */
export async function startTestPayment(identity: CallerIdentity, ns: string, id: string): Promise<TestPayment> {
  const r = await callGateway<TestPayment | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}/testpay`, { method: "POST", body: {} });
  if (!r.data) throw new Error("The gateway gave no test order; try again.");
  return r.data;
}

/** Ask the gateway whether the test order was paid. */
export async function confirmTestPayment(identity: CallerIdentity, ns: string, id: string, ref: string, paymentId = "", signature = ""): Promise<boolean> {
  const r = await callGateway<{ paid: boolean } | null>(identity, config().gatewayBase, `${gw(ns)}/accounts/${enc(id)}/testpay/confirm`, {
    method: "POST",
    body: { connector_reference: ref, payment_id: paymentId, signature },
  });
  return !!r.data?.paid;
}

export async function getGatewayAssignments(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<GatewayAssignments> {
  const r = await callGateway<GatewayAssignments | null>(identity, config().gatewayBase, `${gw(ns)}/assignments`, signal ? { signal } : {});
  return r.data ?? { assignments: [], effective: null };
}

/** Set (gatewayAccountId) or clear ("") the default, or one subscriber's override. */
export async function setGatewayAssignment(identity: CallerIdentity, ns: string, subscriberAccountId: string, gatewayAccountId: string): Promise<void> {
  await callGateway<unknown>(identity, config().gatewayBase, `${gw(ns)}/assignments`, {
    method: "PUT",
    body: { subscriber_account_id: subscriberAccountId.trim(), gateway_account_id: gatewayAccountId },
  });
}

/** The words the screen shows for a gateway kind. */
export const KIND_LABEL: Record<GatewayKind, string> = { razorpay: "Razorpay", upi_qr: "UPI QR (manual)", cashfree: "Cashfree" };

/** Razorpay key ids say their mode; radpayment refuses any other shape. */
export function razorpayKeyError(keyId: string): string | null {
  const k = keyId.trim();
  if (k === "") return "Enter the Key ID";
  return /^rzp_(test|live)_\S+$/.test(k) ? null : "A Razorpay Key ID starts rzp_test_ or rzp_live_";
}

/**
 * A Cashfree App ID is letters and digits (TEST… in test mode, CF… for some
 * accounts); radpayment accepts any non-empty id, Cashfree is the real check.
 */
export function cashfreeAppIdError(appId: string): string | null {
  const k = appId.trim();
  if (k === "") return "Enter the App ID";
  return /^[A-Za-z0-9]{6,64}$/.test(k) ? null : "A Cashfree App ID is letters and digits only";
}

/**
 * Cashfree test secrets contain "_test_" (cfsk_ma_test_…), live ones do not.
 * The connector refuses a mismatch with the stated mode, so say it here first.
 */
export function cashfreeSecretModeError(secret: string, mode: "test" | "live"): string | null {
  const s = secret.trim();
  if (s === "") return null;
  if (mode === "test" && !s.includes("_test_")) return "This looks like a live secret, but Test mode is selected";
  if (mode === "live" && s.includes("_test_")) return "This is a test secret, but Live is selected";
  return null;
}

/**
 * The mode radpayment will store (owner 2026-10-25): a Razorpay key says it
 * (rzp_test_ / rzp_live_), a UPI ID is always real money. null = not yet
 * known. radpayment derives it the same way and never trusts a stated one.
 */
export function detectedMode(kind: GatewayKind, keyId: string): "test" | "live" | null {
  if (kind === "upi_qr") return "live";
  const k = keyId.trim();
  if (k.startsWith("rzp_test_")) return "test";
  if (k.startsWith("rzp_live_")) return "live";
  return null;
}

/** What a mode means, in the screen's words. */
export const MODE_LABEL = { test: "Test mode", live: "Live" } as const;

/** A UPI ID: name@bank -- radpayment upiIDPattern, the same shape. */
/** The help under every receipt email field (owner 2026-10-29). */
export const RECEIPT_EMAIL_HELP =
  "Required. When a subscriber has no email on record, Razorpay/Cashfree payment receipts and notifications go to this address instead.";

/** Why the receipt email is verified (owner 2026-10-29). */
export const RECEIPT_VERIFY_HELP =
  "Checkout uses this address only after it is verified, so receipts never go to a mistyped address. Until then, a subscriber with no email of their own must type one at checkout.";

/** needsReceiptVerify: a Razorpay or Cashfree account whose receipt email is set but not proved. */
export function needsReceiptVerify(a: Pick<GatewayAccount, "connectorname" | "receipt_email" | "receipt_email_verified">): boolean {
  return a.connectorname !== "upi_qr" && Boolean(a.receipt_email) && !a.receipt_email_verified;
}

/**
 * radpayment receiptEmailFor's rule, so a bad address is caught before
 * saving: one plain address, up to 254 characters. "" is "required".
 */
export function receiptEmailError(email: string): string | null {
  const e = email.trim();
  if (e === "") return "A receipt email is required.";
  if (e.length > 254 || (e.match(/@/g) ?? []).length !== 1 || /[\s<>,;"]/.test(e) || !/^[^@]+@[^@]+\.[^@]+$/.test(e)) {
    return "Enter one email address.";
  }
  return null;
}

export function upiIdError(upiId: string): string | null {
  const v = upiId.trim();
  if (v === "") return "Enter the UPI ID";
  return /^[A-Za-z0-9][A-Za-z0-9._-]{1,254}@[A-Za-z][A-Za-z0-9]{1,63}$/.test(v) ? null : "A UPI ID looks like name@bank";
}

/* ---- UPI payment notices ---- */

export type NoticeStatus = "Pending" | "Applied" | "Rejected";

/** radpayment model.PaymentNotice. */
export interface UpiNotice {
  id: string;
  namespace: string;
  accountid: string;
  username: string;
  mobile: string;
  groupname: string;
  planlabel: string;
  amount: number;
  upiref: string;
  paidat?: string;
  /** "after" the current plan, "now", or "" (automatic). */
  startmode: string;
  /** Set when the notice pays an invoice (Ankh's customers); planlabel is its number. */
  invoiceid?: string;
  status: NoticeStatus;
  reason?: string;
  orderid?: string;
  createdat: string;
  decidedat?: string;
  decidedby?: string;
}

const nt = (ns: string) => `/payment/noticeapi/${enc(ns)}`;

export async function listUpiNotices(identity: CallerIdentity, ns: string, status: NoticeStatus | "", signal?: AbortSignal): Promise<UpiNotice[]> {
  const q = status ? `?status=${status}` : "";
  const r = await callGateway<UpiNotice[] | null>(identity, config().gatewayBase, `${nt(ns)}${q}`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function rejectUpiNotice(identity: CallerIdentity, ns: string, id: string, reason: string): Promise<UpiNotice> {
  const r = await callGateway<UpiNotice | null>(identity, config().gatewayBase, `${nt(ns)}/notice/${enc(id)}/reject`, { method: "POST", body: { reason: reason.trim() } });
  if (!r.data) throw new Error("The notice was rejected but not returned; reload the page.");
  return r.data;
}

/** radaccess ApplyUPINotice: plan + receipt, then the notice is marked Applied. */
export interface AppliedNotice {
  /** Absent for an invoice notice: radbilling issues that receipt. */
  receiptno?: string;
  invoiceid?: string;
  expiry?: string;
  devices?: string[];
  replayed?: boolean;
  notice?: UpiNotice;
}

export async function applyUpiNotice(identity: CallerIdentity, ns: string, id: string): Promise<AppliedNotice> {
  const r = await callGateway<AppliedNotice | null>(identity, config().gatewayBase, `${nt(ns)}/notice/${enc(id)}/apply`, { method: "POST", body: {} });
  if (!r.data) throw new Error("Apply ran but returned nothing; check the subscriber's receipts before applying again.");
  return r.data;
}

/** What the start choice means, for the queue. */
export function startModeText(mode: string): string {
  if (mode === "after") return "After current plan ends";
  if (mode === "now") return "Now (unused days added)";
  return "Automatic";
}
